// Growth free mode (backend env GROWTH_FREE_MODE, default on): interviews are free
// for everyone, so paywall triggers, "Upgrade" CTAs and pricing sections are hidden.
// The payment code stays in place — everything is gated on this flag.
//
// Sources of truth, in order: `growth_free_mode` on a /subscription/status payload,
// then this store (fed by /subscription/status responses and the public
// /api/growth/config endpoint, cached in localStorage).
import { create } from 'zustand';

const KEY = 'sdl_growth_free_mode';
const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const readCached = () => {
  try {
    const v = localStorage.getItem(KEY);
    if (v === '1') return true;
    if (v === '0') return false;
  } catch {
    // storage unavailable
  }
  return true; // matches the backend default (GROWTH_FREE_MODE=true)
};

export const useGrowthModeStore = create((set) => ({
  freeMode: readCached(),
  setFreeMode: (on) => {
    try { localStorage.setItem(KEY, on ? '1' : '0'); } catch { /* ignore */ }
    set({ freeMode: !!on });
  },
}));

/** Record the flag from any backend payload (ignored unless it's a boolean). */
export function noteGrowthFreeMode(value) {
  if (typeof value !== 'boolean') return;
  const { freeMode, setFreeMode } = useGrowthModeStore.getState();
  if (freeMode !== value) setFreeMode(value);
}

/** Non-hook read, for event handlers. */
export const isGrowthFreeMode = () => useGrowthModeStore.getState().freeMode;

/** Hook. Prefers the flag on a subscription-status payload when it has one. */
export function useGrowthFreeMode(subscriptionStatus) {
  const flag = useGrowthModeStore((s) => s.freeMode);
  if (subscriptionStatus && typeof subscriptionStatus.growth_free_mode === 'boolean') {
    return subscriptionStatus.growth_free_mode;
  }
  return flag;
}

let inflight = null;
/** Fetch the public flag once per page load (works for logged-out visitors too). */
export function refreshGrowthMode() {
  if (inflight || !BACKEND_URL || typeof fetch !== 'function') return inflight;
  inflight = fetch(`${BACKEND_URL}/api/growth/config`)
    .then((r) => (r.ok ? r.json() : null))
    .then((data) => { if (data) noteGrowthFreeMode(data.growth_free_mode); })
    .catch(() => {});
  return inflight;
}
